import React, { createContext, useCallback, useState, useContext } from 'react';
import { toast } from 'react-toastify';
import jwt from 'jsonwebtoken';
import { apiCustomer } from '../../services/apiCustomer';
import {
  removeSessionStorage,
  saveSessionStorage,
  getSessionStorage,
  encrypt,
  decrypt,
  seveOrUpdateDataAppForNextApp,
} from '../../services/functions';
import { Customer } from '../../services/types';

interface AuthState {
  token: string;
  customer: Customer;
}

interface SignInCredentials {
  username: string;
  password: string;
  close(): void;
  messageWhenFinished?: boolean
}

interface AuthContextData {
  customer: Customer;
  signIn(credentials: SignInCredentials): Promise<void>;
  signOut(toMarketplace: boolean): void;
  isLoading: boolean;
  setCustomerData(customer: Customer): void;
}

interface AuthProviderProps {
  children: any;
}

export const AuthContext = createContext<AuthContextData>(
  {} as AuthContextData,
);

export const AuthProvider: React.FC<AuthProviderProps> = ({
  children,
}: AuthProviderProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState<AuthState>(() => {
    const token = getSessionStorage('token') || '';
    const customer = getSessionStorage('customer') || '';
    const decryptedToken = decrypt(token);
    const decryptedCustomer = decrypt(customer);

    if (token && customer) {
      return { token: decryptedToken, customer: JSON.parse(decryptedCustomer) };
    }
    return {} as AuthState;
  });

  const setCustomerData = useCallback((customer: Customer) => {
    const encryptedToken = getSessionStorage('token');
    if (encryptedToken) {
      const decryptToken = decrypt(encryptedToken);
      const token: any = jwt.decode(decryptToken);
      removeSessionStorage('customer');
      const encryptedCustomer = encrypt(customer, true);
      saveSessionStorage('customer', encryptedCustomer);
      setData({ token, customer });
    }
  }, []);

  // singOut
  const signOut = useCallback((toMarketplace: boolean) => {
    removeSessionStorage('token');
    removeSessionStorage('customer');
    removeSessionStorage('credentials');
    removeSessionStorage('tokenExpirationDate');
    // console.log('CRA: toMarketplace', toMarketplace);
    setData({} as AuthState);
    if (toMarketplace) {
      // toast.error('Usuário deslogado com sucesso!');
      // history.push('/test');
      // window.location.reload();
      // Router.push('http://localhost:4000/')
      const dataApp = {
        authenticated: false,
        activeToastSignOut: true,
        toastSignOutMessage: 'Usuário deslogado com sucesso!',
        info: 'signOu in CRA',
      };
      seveOrUpdateDataAppForNextApp(dataApp);
      // window.location.href = '/';
      // setTimeout(() => {
      //   window.location.href = '/';
      // }, 1000);
    } else {
      toast.success('Usuário deslogado com sucesso!');
    }
  }, []);
  // Adicionado Função pra checar se o token está expirado

  const signIn = useCallback(
    async ({ username, password, close, messageWhenFinished = true }) => {
      setIsLoading(true);
      await apiCustomer
        .post('auth/token', {
          username,
          password,
        })
        .then(response => {
          const { token } = response.data;
          const decodedToken: any = jwt.decode(token);
          const customerId = decodedToken.sub;
          const expirationToken = decodedToken.exp;
          const encryptedToken = encrypt(token).toString();
          saveSessionStorage('token', encryptedToken);
          saveSessionStorage('tokenExpirationDate', expirationToken);
          apiCustomer.defaults.headers.Authorization = `Bearer ${token}`;
          apiCustomer
            .get<Customer>(`customers/${customerId}`)
            .then(res => {
              const customer = {
                id: res.data.id,
                name: res.data.name,
                email: res.data.email,
                cpf: res.data.cpf,
                phoneDdd: res.data.phoneDdd,
                phoneNumber: res.data.phoneNumber,
                avatarPath: res.data.avatarPath,
              };
              const encryptedCustomer = encrypt(customer, true);
              const encryptedCredentials = encrypt(
                { username, password },
                true,
              );
              saveSessionStorage('credentials', encryptedCredentials);
              saveSessionStorage('customer', encryptedCustomer);
              setData({ token, customer });
              if(messageWhenFinished) {
                finishRequest(true);
                close();
              } else {
                setIsLoading(false);
              }
            })
            .catch(err => {
              console.log(err.response.data);
              signOut(true);
              finishRequest(false);
            });
        })
        .catch(err => {
          console.log(err.response.data);
          finishRequest(false);
        });
    },
    [signOut],
  );

  const finishRequest = (status: boolean) => {
    setIsLoading(false);
    if (status) {
      toast.success('Usuário logado com sucesso!');
    } else {
      toast.error('Usuário ou senha inválidos');
    }
  };

  return (
    <AuthContext.Provider
      value={{
        customer: data.customer,
        signIn,
        signOut,
        isLoading,
        setCustomerData,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
export function useAuth(): AuthContextData {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within  an AuthProvider');
  }

  return context;
}
